import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { transport } from '@opengov/app-core/transport';
import { useQueryClient } from '@tanstack/react-query';
import SvgIcon from '@mui/material/SvgIcon';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Close as CloseIcon } from '@opengov/react-capital-assets';
import React, { useEffect, useState } from 'react';

import { IBudget, IProposal, IStory, IStoryContent } from '@opengov/stories-shared/types';

import { MergeTagsOperation } from '../../../stories/common/context/MergeTagsContext';
import {
  useAddProposalToStory,
  useUpdateAllStoryContentsMergeTagsDB
} from '../../../stories/components/StoryView/hooks';
import { IModalMessages } from '../../../types';
import { styles } from './AddProposalModal.styles';

type IProposalOption = IProposal & { disabled?: boolean };

export type IStoryBudget = {
  id: number;
  title: string;
  disabled?: boolean;
};

export type IAddProposalModalProps = {
  messages: IModalMessages;
  selectedStoryId: string;
  budget: IBudget;
  isTemplate: boolean;
  story: IStory;
  publicationId?: string;
  userId?: number;
  storyContents: IStoryContent[];
  entityId: number;
  onClose(): void;
};

export const AddProposalModal: React.FC<IAddProposalModalProps> = (props: IAddProposalModalProps) => {
  const {
    selectedStoryId,
    messages,
    budget,
    isTemplate,
    story,
    publicationId,
    userId,
    storyContents,
    entityId,
    onClose
  } = props;

  const queryClient = useQueryClient();
  const { mutateAsync: doAddProposal } = useAddProposalToStory();
  const { mutateAsync: doUpdateMergeTags } = useUpdateAllStoryContentsMergeTagsDB();

  const [selectedBudget, setSelectedBudget] = useState<IStoryBudget | null>(
    budget ? { id: budget.budgetId, title: budget.budgetTitle } : null
  );
  const [budgetsList, setBudgetsList] = useState<IStoryBudget[]>([]);
  const [isLoadingBudget, setIsLoadingBudget] = useState<boolean>(false);

  const [proposalsList, setProposalsList] = useState<IProposalOption[]>([]);
  const [selectedProposal, setSelectedProposal] = useState<IProposal | null>(null);
  const [isLoadingProposals, setIsLoadingProposals] = useState<boolean>(false);

  const [isAddingProposals, setIsAddingProposals] = useState<boolean>(false);

  const getAllProposals = (budgetSelected: IStoryBudget | null) => {
    if (budgetSelected) {
      setIsLoadingProposals(true);
      void transport
        .url(`/api/v3/proposals?limit=1000&offset=0&budget_id=${budgetSelected.id}`)
        .get()
        .json<{ proposals: IProposal[] }>()
        .then((response) => {
          const proposals = response?.proposals || [];
          const selectedProposalIds = budget?.proposals.map((p) => p.proposalId) ?? [];
          setProposalsList(
            proposals.map((proposal) => ({
              ...proposal,
              disabled: selectedProposalIds.includes(proposal.id)
            }))
          );
        })
        .finally(() => setIsLoadingProposals(false));
    }
  };

  useEffect(() => {
    if (!budgetsList.length) {
      setIsLoadingBudget(true);
      void transport
        .url(`/api/v3/budgets?entity_id=${entityId}&limited_attributes=id,title`)
        .get()
        .json<{ budgets: IStoryBudget[] }>()
        .then((response) => {
          setBudgetsList(response?.budgets || []);
        })
        .finally(() => setIsLoadingBudget(false));
    }
  }, [budgetsList.length, entityId]);

  useEffect(() => {
    getAllProposals(selectedBudget);
  }, [selectedBudget]);

  const onChangeBudgetSelection = (budget: IStoryBudget | null) => {
    setProposalsList([]);
    setSelectedProposal(null);
    setSelectedBudget(budget);
  };

  const onChangeProposalSelection = (proposal: IProposal) => {
    proposal ? setSelectedProposal(proposal) : setSelectedProposal(null);
  };

  const renderBudgetDropdown = () => {
    return (
      <Box sx={styles.dropdownContainer}>
        <Typography id="add-proposal-budget-label" variant="body2">{messages.budget}</Typography>
        {!!budget ? (
          <Typography variant="body2">{budget.budgetTitle}</Typography>
        ) : (
          <Autocomplete
            options={budgetsList ?? []}
            getOptionLabel={(option) => option.title}
            value={selectedBudget}
            onChange={(_event, value) => onChangeBudgetSelection(value)}
            size="small"
            disablePortal
            loading={isLoadingBudget}
            renderOption={(props, option) => <li {...props}>{option.title}</li>}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={messages.enterBudget}
                inputProps={{ ...params.inputProps, 'aria-labelledby': 'add-proposal-budget-label', className: 'no-format' }}
              />
            )}
          />
        )}
      </Box>
    );
  };

  const renderProposalDropdown = () => {
    return (
      <Box sx={styles.dropdownContainer}>
        <Typography id="add-proposal-proposals-label" variant="body2">{messages.proposals}</Typography>
        <Autocomplete
          options={proposalsList ?? []}
          getOptionLabel={(option) => option.title}
          value={selectedProposal}
          onChange={(_event, value) => onChangeProposalSelection(value)}
          size="small"
          disablePortal
          loading={isLoadingProposals}
          getOptionDisabled={(option) => !!(option as IProposalOption).disabled}
          renderOption={(props, option) => <li {...props}>{option.title}</li>}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={messages.enterProposals}
              inputProps={{ ...params.inputProps, 'aria-labelledby': 'add-proposal-proposals-label', className: 'no-format' }}
            />
          )}
        />
      </Box>
    );
  };

  const addButtonAction = async () => {
    setIsAddingProposals(true);
    try {
      const { mergeTags } = await doAddProposal({
        budgetId: selectedBudget!.id,
        proposalId: selectedProposal!.id,
        storyId: selectedStoryId,
        entityId
      });
      await doUpdateMergeTags({
        isTemplate,
        story,
        storyContents,
        publicationId: publicationId ?? '',
        userId: userId ?? 0,
        storyMergeTagsOperation: { operation: MergeTagsOperation.Update, mergeTags }
      });
      void queryClient.invalidateQueries({ queryKey: ['storyView', 'proposals'] });
    } finally {
      setIsAddingProposals(false);
      onClose();
    }
  };

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: styles.dialogPaper }} aria-labelledby="add-proposal-dialog-title">
      <DialogTitle id="add-proposal-dialog-title" sx={styles.dialogTitle}>
        {messages.addProposals}
        <IconButton aria-label="close" onClick={onClose} size="small" sx={styles.closeButton}>
          <SvgIcon component={CloseIcon} inheritViewBox />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        {renderBudgetDropdown()}
        {renderProposalDropdown()}
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          data-test="add-proposal-cancel-button"
          onClick={onClose}
        >
          {messages.cancel}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={!selectedProposal}
          data-test="add-proposal-button"
          onClick={addButtonAction}
          startIcon={isAddingProposals ? <CircularProgress size={16} color="inherit" aria-label="Loading" /> : undefined}
        >
          {messages.add}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
